import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CardComponent } from '@3ddv/software-division-components/backoffice/card';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { TagifyService, TagifySettings, TagifyComponent } from 'ngx-tagify';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { AuthStore } from '../../core/stores/auth.store';
import { GsLoaderComponent } from '../../shared/ui/loader/gs-loader.component';
import { Client } from '../events/models/client.model';
import { PrivateSellingService } from '../group-links/services/private-selling.service';
import { UtilsService } from '../services/utils.service';
import {
  ClientSettingsService,
  GeneralSettingsClientPatch,
} from './services/client-settings.service';

@Component({
  selector: 'app-general-settings',
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.css'],
  imports: [
    ReactiveFormsModule,
    TagifyComponent,
    CardComponent,
    InputComponent,
    ButtonComponent,
    GsLoaderComponent,
  ],
})
export class GeneralSettingsComponent implements OnInit, OnDestroy {
  private readonly privateSellingService = inject(PrivateSellingService);
  private readonly clientSettingsService = inject(ClientSettingsService);
  private readonly utils = inject(UtilsService);
  private readonly toastr = inject(ToastrService);
  protected readonly authStore = inject(AuthStore);
  private readonly tagifyService = inject(TagifyService);
  private readonly subscriptions = new Subscription();

  private static readonly holdCodesAllowedKeys = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);

  public loading = true;
  public saving = false;

  public readonly buyerTypeNameControl = new FormControl<string>('', {
    nonNullable: true,
  });
  public readonly buyerTypeIdControl = new FormControl<string | null>(null);
  public readonly minTicketsControl = new FormControl<string | null>(null);
  public readonly maxTicketsControl = new FormControl<string | null>(null);

  public adaHoldCodesForm = new FormControl<{ value: string }[]>([]);
  public regularHoldCodesForm = new FormControl<{ value: string }[]>([]);

  public readonly regularHoldCodesSettings =
    this.createHoldCodesSettings('regular-hold-codes');
  public readonly adaHoldCodesSettings =
    this.createHoldCodesSettings('ada-hold-codes');

  private createHoldCodesSettings(tagifyName: string): TagifySettings {
    return {
      placeholder: 'Press enter to insert...',
      trim: true,
      duplicates: false,
      delimiters: /,|\s|\n/,
      pasteAsTags: true,
      keepInvalidTags: false,
      editTags: false,
      dropdown: { enabled: false },
      pattern: /^[0-9]+$/,
      transformTag: tagData => {
        const t = tagData as { value?: string };
        t.value = String(t.value ?? '').replace(/\D/g, '');
      },
      validate: tagData => {
        const v = String((tagData as { value?: string }).value ?? '');
        return v.length > 0 && /^\d+$/.test(v);
      },
      hooks: {
        beforeKeyDown: (event: KeyboardEvent) => {
          if (event.ctrlKey || event.metaKey || event.altKey) {
            return Promise.resolve();
          }
          if (GeneralSettingsComponent.holdCodesAllowedKeys.has(event.key)) {
            return Promise.resolve();
          }
          if (event.key.length === 1 && !/^\d$/.test(event.key)) {
            event.preventDefault();
            return Promise.reject();
          }
          return Promise.resolve();
        },
        beforePaste: async (_event: ClipboardEvent, data) => {
          return data.pastedText.replace(/\D/g, '') || undefined;
        },
      },
      callbacks: {
        invalid: (event: CustomEvent) => this.onInvalidHoldTag(event),
        input: (e: CustomEvent) => {
          const detail = e.detail as { value?: string };
          const raw = String(detail?.value ?? '');
          const digits = raw.replace(/\D/g, '');
          if (raw === digits) {
            return;
          }
          const tagify = this.tagifyService.get(tagifyName);
          if (!tagify) {
            return;
          }
          queueMicrotask(() => {
            (
              tagify as unknown as {
                input: { set: (value?: string, updateDOM?: boolean) => void };
              }
            ).input.set(digits, true);
          });
        },
      },
    };
  }

  public hashValidator = {
    defaultBuyerTypeId: false,
    defaultBuyerTypeName: false,
    minTickets: false,
    maxTickets: false,
    minMaxOrder: false,
  };

  private clientId: number | null = null;
  private initialSnapshot: {
    defaultBuyerTypeName: string;
    defaultBuyerTypeId: number | null;
    minTickets: number | null;
    maxTickets: number | null;
    regularHoldCodes: string[];
    adaHoldCodes: string[];
  } | null = null;

  public ngOnInit(): void {
    const sub = this.privateSellingService
      .getClientWithWhitelist(null, true)
      .pipe(
        mergeMap(res => {
          const id = res.data?.id;
          if (id == null) {
            this.toastr.error('Could not resolve client');
            this.loading = false;
            return EMPTY;
          }
          this.clientId = id;
          return this.clientSettingsService.get$(id);
        })
      )
      .subscribe({
        next: res => {
          this.applyClient(res.data);
          this.loading = false;
        },
        error: err => {
          this.loading = false;
          this.toastr.error(
            err.error?.error?.message ||
              err.error?.message ||
              err.message ||
              'Failed to load general settings'
          );
        },
      });
    this.subscriptions.add(sub);
  }

  private applyClient(data: Client): void {
    this.buyerTypeNameControl.setValue(data.defaultBuyerTypeName ?? '');
    this.buyerTypeIdControl.setValue(
      data.defaultBuyerTypeId != null ? String(data.defaultBuyerTypeId) : null
    );
    this.minTicketsControl.setValue(
      data.minTickets != null ? String(data.minTickets) : null
    );
    this.maxTicketsControl.setValue(
      data.maxTickets != null ? String(data.maxTickets) : null
    );

    const regular = data.regularHoldCodes ?? [];
    const ada = data.adaHoldCodes ?? [];
    this.regularHoldCodesForm.setValue(
      regular.map(code => ({ value: String(code) }))
    );
    this.adaHoldCodesForm.setValue(ada.map(code => ({ value: String(code) })));

    this.initialSnapshot = {
      defaultBuyerTypeName: data.defaultBuyerTypeName ?? '',
      defaultBuyerTypeId: data.defaultBuyerTypeId ?? null,
      minTickets: data.minTickets ?? null,
      maxTickets: data.maxTickets ?? null,
      regularHoldCodes: [...regular],
      adaHoldCodes: [...ada],
    };
    this.applyControlErrors();
  }

  private getHoldCodesData(form: FormControl): string[] {
    const formValue = form.value || [];
    return formValue.map((item: string | { value: string }) =>
      typeof item === 'string' ? item : item.value
    );
  }

  private onInvalidHoldTag(_event: CustomEvent): void {
    this.toastr.error(
      'Invalid hold code format. Please enter numeric codes only.'
    );
  }

  private numericValue(control: FormControl<string | null>): number | null {
    const raw = control.value;
    if (raw == null || raw === '') {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private applyControlErrors(): void {
    this.buyerTypeNameControl.setErrors(
      this.hashValidator.defaultBuyerTypeName ? { required: true } : null
    );
    this.buyerTypeIdControl.setErrors(
      this.hashValidator.defaultBuyerTypeId ? { numeric: true } : null
    );
    const orderError = this.hashValidator.minMaxOrder;
    this.minTicketsControl.setErrors(
      !orderError && this.hashValidator.minTickets ? { numeric: true } : null
    );
    this.maxTicketsControl.setErrors(
      !orderError && this.hashValidator.maxTickets ? { numeric: true } : null
    );
  }

  private validateForm(): boolean {
    this.hashValidator = {
      defaultBuyerTypeId: false,
      defaultBuyerTypeName: false,
      minTickets: false,
      maxTickets: false,
      minMaxOrder: false,
    };
    let hasErrors = false;

    if (this.utils.isEmpty(this.buyerTypeNameControl.value)) {
      this.hashValidator.defaultBuyerTypeName = true;
      hasErrors = true;
    }

    const idStr = this.buyerTypeIdControl.value ?? '';
    if (!this.utils.onlyHasNumbers(idStr) || Number(idStr) <= 0) {
      this.hashValidator.defaultBuyerTypeId = true;
      hasErrors = true;
    }

    const min = this.numericValue(this.minTicketsControl);
    const max = this.numericValue(this.maxTicketsControl);

    if (min == null || !Number.isInteger(min) || min < 1) {
      this.hashValidator.minTickets = true;
      hasErrors = true;
    }
    if (max == null || !Number.isInteger(max) || max < 1) {
      this.hashValidator.maxTickets = true;
      hasErrors = true;
    }
    if (!hasErrors && min != null && max != null && min > max) {
      this.hashValidator.minMaxOrder = true;
      this.hashValidator.minTickets = true;
      this.hashValidator.maxTickets = true;
      hasErrors = true;
    }

    this.applyControlErrors();
    return !hasErrors;
  }

  private buildPatch(): GeneralSettingsClientPatch | null {
    if (!this.initialSnapshot) {
      return null;
    }

    const regular = this.getHoldCodesData(this.regularHoldCodesForm);
    const ada = this.getHoldCodesData(this.adaHoldCodesForm);

    const patch: GeneralSettingsClientPatch = {};

    const buyerName = this.buyerTypeNameControl.value ?? '';
    const buyerTypeId = this.numericValue(this.buyerTypeIdControl);
    const min = this.numericValue(this.minTicketsControl);
    const max = this.numericValue(this.maxTicketsControl);

    const buyerChanged =
      buyerName !== this.initialSnapshot.defaultBuyerTypeName ||
      buyerTypeId !== this.initialSnapshot.defaultBuyerTypeId;
    if (buyerChanged) {
      patch.defaultBuyerTypeId = buyerTypeId!;
      patch.defaultBuyerTypeName = buyerName;
    }

    if (min !== this.initialSnapshot.minTickets) {
      patch.minTickets = min!;
    }
    if (max !== this.initialSnapshot.maxTickets) {
      patch.maxTickets = max!;
    }

    if (!this.arraysEqual(regular, this.initialSnapshot.regularHoldCodes)) {
      patch.regularHoldCodes = regular;
    }
    if (!this.arraysEqual(ada, this.initialSnapshot.adaHoldCodes)) {
      patch.adaHoldCodes = ada;
    }

    return Object.keys(patch).length > 0 ? patch : null;
  }

  private arraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((v, i) => v === b[i]);
  }

  public save(): void {
    if (!this.validateForm() || this.clientId == null) {
      return;
    }

    const patch = this.buildPatch();
    if (!patch) {
      this.toastr.info('No changes to save');
      return;
    }

    this.saving = true;
    const saveSub = this.clientSettingsService
      .patch$(this.clientId, patch)
      .subscribe({
        next: res => {
          this.saving = false;
          this.applyClient(res.data);
          this.privateSellingService
            .getClientWithWhitelist(null, true)
            .subscribe({ error: () => {} });
          this.toastr.success('General settings saved');
        },
        error: err => {
          this.saving = false;
          this.toastr.error(
            err.error?.error?.message ||
              err.error?.message ||
              err.message ||
              'Failed to save general settings'
          );
        },
      });
    this.subscriptions.add(saveSub);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
